import { orderBy, sortBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { DataGridCRUDOnAddNewRowCallback } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDActions';
import { Enum } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  CompanyEquipmentEntry,
  EmployeeEquipmentTableRow,
  EquipmentEntryType,
} from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/data';

export enum NewEquipmentDialogMode {
  ExistingCompanyEquipment = 'existing',
  AddNewCompanyEquipment = 'addNew',
}

type EquipmentFormType = Pick<
  EmployeeEquipmentTableRow,
  'type' | 'item' | 'inventoryNumber' | 'companyEquipmentId'
>;

export type UseNewEquipmentDialogProps = {
  equipmentTypes: Enum[];
  companyEquipment: CompanyEquipmentEntry[];
  onAddNewRow: DataGridCRUDOnAddNewRowCallback<EmployeeEquipmentTableRow>;
  nextEquipmentNumber: string;
  updateUsedEquipmentNumber: (
    inventoryNumber: string,
  ) => Promise<string | undefined>;
};

export const useNewEquipmentDialog = (
  props: UseNewEquipmentDialogProps & { onClose: () => void },
) => {
  const {
    equipmentTypes,
    companyEquipment,
    onAddNewRow,
    nextEquipmentNumber,
    updateUsedEquipmentNumber,
    onClose: onDialogClose,
  } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const companyEquipmentOptions = useMemo(() => {
    const typeCodes = sortBy(equipmentTypes, ['seq', 'name']).map(
      (et) => et.code,
    );

    return orderBy(
      companyEquipment,
      [(ce) => typeCodes.indexOf(ce.type), 'inventoryNumber'],
      ['asc', 'desc'],
    );
  }, [companyEquipment, equipmentTypes]);

  // Mode
  const [newEquipmentMode, setNewEquipmentMode] =
    useState<NewEquipmentDialogMode>(
      NewEquipmentDialogMode.ExistingCompanyEquipment,
    );

  const onToggleMode = useCallback(
    (mode: NewEquipmentDialogMode) =>
      (_event: SyntheticEvent, isCollapsed: boolean) => {
        if (isCollapsed) {
          setNewEquipmentMode(mode);
        }
      },
    [setNewEquipmentMode],
  );

  //
  const getInitialFormValues = useCallback(
    (inventoryNumber: string): EquipmentFormType => {
      return {
        companyEquipmentId: -1,
        type: '',
        item: '',
        inventoryNumber,
      };
    },
    [],
  );

  // Validation
  const validationSchema = useMemo(() => {
    switch (newEquipmentMode) {
      case NewEquipmentDialogMode.AddNewCompanyEquipment:
        return yup.object().shape<EquipmentFormType>({
          companyEquipmentId: yup.number().integer().defined(),
          type: yup
            .string()
            .required(t('laFillManFields'))
            .oneOf(
              equipmentTypes.map((type) => type.code),
              t('laFillManFields'),
            ),
          item: yup.string(),
          inventoryNumber: yup.string().required(t('laFillManFields')),
        });
      case NewEquipmentDialogMode.ExistingCompanyEquipment:
      default:
        return yup.object().shape<EquipmentFormType>({
          companyEquipmentId: yup
            .number()
            .integer()
            .required(t('laFillManFields'))
            .oneOf(
              companyEquipment.map((equipment) => equipment.id),
              t('laFillManFields'),
            ),
          type: yup.string(),
          item: yup.string(),
          inventoryNumber: yup.string(),
        });
    }
  }, [t, newEquipmentMode, equipmentTypes, companyEquipment]);

  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<EquipmentFormType>({
    resolver,
    defaultValues: getInitialFormValues(nextEquipmentNumber),
  });
  const { handleSubmit, reset } = form;

  const handleClose = useCallback(
    (resetData?: EquipmentFormType) => {
      reset(resetData);
      onDialogClose();
    },
    [reset, onDialogClose],
  );

  const resolveEquipment = useCallback(
    (
      data: EquipmentFormType,
    ): EquipmentFormType | EmployeeEquipmentTableRow | undefined => {
      const resolveNewEquipment = (): EquipmentFormType => {
        return {
          ...data,
          companyEquipmentId: -1,
        };
      };

      const resolveExistingEquipment = (
        companyEquipment?: CompanyEquipmentEntry,
      ): EmployeeEquipmentTableRow | undefined => {
        if (!!companyEquipment) {
          return {
            ...companyEquipment,
            ...data,
            id: -1,
            entryType: EquipmentEntryType.Employee,
            deliveryDate: undefined,
            type: companyEquipment.type,
            item: companyEquipment.item,
            inventoryNumber: companyEquipment.inventoryNumber,
          };
        }

        return undefined;
      };

      switch (newEquipmentMode) {
        case NewEquipmentDialogMode.AddNewCompanyEquipment:
          return resolveNewEquipment();
        case NewEquipmentDialogMode.ExistingCompanyEquipment:
          return resolveExistingEquipment(
            companyEquipment.find(
              (equipment) => equipment.id === data.companyEquipmentId,
            ),
          );
      }
    },
    [newEquipmentMode, companyEquipment],
  );

  const handleUpdateNextInventoryNumber = useCallback(
    (
      equipment: EquipmentFormType | EmployeeEquipmentTableRow,
    ):
      | Promise<{
          equipment: EquipmentFormType | EmployeeEquipmentTableRow;
          nextNumber: string;
        }>
      | {
          equipment: EquipmentFormType | EmployeeEquipmentTableRow;
          nextNumber: string;
        } => {
      if (
        equipment.companyEquipmentId === -1 &&
        equipment.inventoryNumber === nextEquipmentNumber
      ) {
        return updateUsedEquipmentNumber(equipment.inventoryNumber).then(
          (newNext) => {
            return { equipment, nextNumber: newNext ?? nextEquipmentNumber };
          },
        );
      }

      return { equipment, nextNumber: nextEquipmentNumber };
    },
    [nextEquipmentNumber, updateUsedEquipmentNumber],
  );

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      new Promise<EquipmentFormType>((resolve, reject) => {
        const equipment = resolveEquipment(data);

        if (!!equipment) {
          resolve(equipment);
        } else {
          reject('Failed to resolve equipment information');
        }
      })
        .then(handleUpdateNextInventoryNumber)
        .then(({ equipment, nextNumber }) => {
          onAddNewRow((defaultRow): EmployeeEquipmentTableRow => {
            return {
              ...defaultRow,
              ...equipment,
              attachmentId: -1,
              attachmentName: '',
              attachmentIsArchived: false,
              id: defaultRow.id,
            };
          });
          handleClose(getInitialFormValues(nextNumber));
        })
        .catch((err) => {
          enqueueSnackbar(err, {
            variant: 'error',
          });
        });
    });
  }, [
    handleSubmit,
    resolveEquipment,
    onAddNewRow,
    handleClose,
    enqueueSnackbar,
    handleUpdateNextInventoryNumber,
    getInitialFormValues,
  ]);

  return {
    form,
    dialogMode: {
      mode: newEquipmentMode,
      onToggleMode,
    },
    equipmentTypes,
    companyEquipmentOptions,
    nextEquipmentNumber,
    onSubmit,
    onClose: () => handleClose(),
  };
};
